/* tslint:disable:max-line-length */
import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewEncapsulation} from '@angular/core';
import {TaskManagerState} from '../../store/task-manager.reducer';
import {getStatusList, getTaskViewUpdateInfo} from '../../store/task-manager.selector';
import {QueryProvider} from '@shared/queries/query-provider';
import {IQueryState} from '@shared/store/reducers/query.reducer';
import {getQueryResponse} from '@shared/store/selectors/query.selector';
import {Observable, Subject} from 'rxjs';
import {take, takeUntil} from 'rxjs/operators';
import {TimeZoneService} from '@core/services/time-zone.service';
import {SecurityState} from '@shared/store/reducers/security.reducer';
import {getUserInfo, hasFeature} from '@shared/store/selectors/security.selector';
import {select, Store} from '@ngrx/store';
import {SupportCompanyId, TicketOwner, TicketReviewer} from '../../constants/task-manager.constants';
import {IMenuItem} from '@shared/interfaces/menu-item.interface';
import {TaskManagerService} from '@features/task-manager/services/task-manager.service';
import {CommandService} from '@core/services/command.service';
import {UpdateStatusCommand} from '@features/task-manager/commands/task-manager.command';
import {PlatformCommand} from '@shared/commands/command-base';
import {Api} from '@shared/constants/common-field.constants';
import {PlatformQueryService} from '@core/services/platform-query.service';
import {PlatformQuery} from '@shared/models/platform-query/platform-query';
import {SnackBarService} from '@core/services/snack-bar.service';
import {Router} from '@angular/router';
import {AuthApiService} from '@core/services/auth-api.service';
import {SocketClusterService} from '@core/services/socket-cluster.service';
import {Configuration} from '@shared/models/configuration';

@Component({
    standalone: false,
    selector: 'particle-task-detail-view',
  templateUrl: './task-detail-view.component.html',
  styleUrls: ['./task-detail-view.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class TaskDetailViewComponent implements OnInit, OnDestroy {

  id = 'task-details-view';
  hasFeature$: Observable<any>;
  isClose = false;

  // Board
  boardList$: any;
  currentBoard: any = {};
  supportBoardId: string = 'orbitax_support';
  currentBoardId: string;

  // Status
  isStatusHidden = false;
  hasPermissionForChangeStatus = true;
  statusList = [];
  selectedStatus = '';
  statusHidden = false;

  // priorities
  priorities: any;
  hasPriorities: boolean;
  selectedPriority: any;
  priorityHidden: boolean;

  // StarDate and Due Date
  startDate: any;
  dueDate: any;

  // people roles notification subtasks
  taskInfo = {};

  // common value
  userInfo: any;
  isSupportUser = false;

  hasPermission = true;

  // Input Values
  @Input() dynamicData: any;
  @Input() card: any;
  @Input() data: any;
  @Input() taskMenuItems: any = {};

  // HelperValues
  unsubscribe$: Subject<void>;

  //output event
  @Output() selectedMenuChange$: EventEmitter<IMenuItem> = new EventEmitter();

  startDateHeader: string = 'Start Date';
  dueDateHeader: string = 'Due Date';
  startDateLabel: string = 'Add';
  dueDateLabel: string = 'Add';

  magicLinkAccessInfo: any;
  crossCompanyAccessInfo: any;
  crossCompanyAccessModelStatus: any = null;
  isRequestedByMe = false;
  supportClientAccessEnabled = TaskDetailViewComponent.readSupportClientAccessEnabled();

  constructor(private taskManagerStore: Store<TaskManagerState>,
              private queryStore: Store<IQueryState>,
              private timeZoneService: TimeZoneService,
              private taskManagerService: TaskManagerService,
              protected commandService: CommandService,
              private platformQueryService: PlatformQueryService,
              private authApiService: AuthApiService,
              private securityStore: Store<SecurityState>,
              private snackBarService: SnackBarService,
              private socketClusterService: SocketClusterService,
              private router: Router,
  ) {
    this.unsubscribe$ = new Subject<void>();

  }

  private static readSupportClientAccessEnabled(): boolean {
    try {
      return Configuration.getConfiguration('EnableSupportClientAccess') === true;
    } catch {
      return false;
    }
  }

  ngOnDestroy(): void {
    this.socketClusterService.unsubscribeChannel(this.card.id);
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  ngOnInit() {

    if (this.isSupportBoard()) {
      this.startDateHeader = 'Opened';
      this.dueDateHeader = 'Closed';
      this.startDateLabel = 'N/A';
      this.dueDateLabel = 'N/A';
    }
    this.currentBoardId = this.data?.input?.board?.id;
    this.hasFeature$ = this.securityStore.pipe(select(hasFeature), takeUntil(this.unsubscribe$));
    if (this.data.input) {
      this.dynamicData = this.data.input;
    }
    if (this.dynamicData && this.dynamicData.card) {
      this.card = this.dynamicData.card;
      this.setPermission();
    }
    this.setPriorityDropdownItems();
    this.setUserInfo();
    this.setBoardInformation();
    this.setStatusDropdownItems();
    this.setStartDateAndDueDate();
    this.subscribeToInvalidatorListener();
    this.initializeCrossCompanyAccessInfo();
    this.subscribeToAccessRequestUpdate();

  }

  private setUserInfo() {
    this.securityStore.pipe(select(getUserInfo)).subscribe((userInfo => {
      const isFirstLoad = !this.userInfo;
      this.userInfo = userInfo;
      if (this.userInfo) {
        this.isSupportUser = userInfo.companyId.toLowerCase() === 'orbitaxsupport'; // TODO: Comment left by advise from Masud vai
        this.hidePriorityIfNeeded();
        this.setPermissionForSupportBoard(this.isSupportUser);
        this.modifyStatusHiddenProperty();
        if (isFirstLoad && this.card) {
          this.getCrossCompanyAccessStatusByCard();
        }
      }
    }));
  }

  private setPermissionForSupportBoard(isSupportUser: boolean) {
    if (this.isSupportBoard()) {
      this.hasPermissionForChangeStatus = isSupportUser == true;
    }
  }

  private setPermission() {
    this.hasPermission = this.card && this.card.hasPermission;
    this.hasPermissionForChangeStatus = this.card
      && (TicketOwner <= this.card.permissionLevel && this.card.permissionLevel <= TicketReviewer);
    if (this.card && this.card.fieldValues && this.card.fieldValues.length > 0) {
      const hasFlowIdKey = (ele) => ele.Key === 'FlowId';
      this.statusHidden = this.card.fieldValues.findIndex(hasFlowIdKey) > -1;
    }
  }

  // Board Getting Functions

  private isSupportBoard() {
    return this.data?.input?.board?.id === this.supportBoardId;
  }

  setBoardInformation() {
    const boardQueryKey = 'allBoards';
    this.boardList$ = this.queryStore.pipe(select(getQueryResponse, boardQueryKey));
    this.boardList$.pipe(takeUntil(this.unsubscribe$)).subscribe(state => {
      console.log(state);
      if (state) {
        this.currentBoard = state.items[0];
        if (this.dynamicData && this.dynamicData.board) {
          this.currentBoard = state.items.find(o => o.id === this.dynamicData.board.id);
        }
      }
    });
    QueryProvider.invokeQuery(this.queryStore, boardQueryKey, {});
  }

  // Status Function

  modifyStatusHiddenProperty() {
    if (!this.isSupportUser && this.card && this.card.scopeInt === 3) {
      this.isStatusHidden = true;
    }
  }

  setStatusDropdownItems() {
    this.taskManagerStore.select(getStatusList).pipe(takeUntil(this.unsubscribe$)).subscribe(currentStatusList => {
      this.setSelectedStatusList(currentStatusList);
    });
  }

  handleStatusChange($event) {
    var oldStatus = this.card.status;
    this.card.status = $event;
    this.updateStatus(oldStatus);
  }

  // priorityStart

  private hidePriorityIfNeeded() {
    if (this.isSupportBoard()) {
      this.priorityHidden = !this.hasPriorities || !this.isSupportUser;
    }
  }

  private setPriorityDropdownItems() {
    if (this.card?.priority && this.card?.board?.priorities) {
      this.hasPriorities = true;
      this.priorities = this.card.board.priorities.sort((a, b) => {
        return a.order - b.order;
      });
      this.setSelectedPriority();
    }
  }

  private setSelectedPriority() {
    this.selectedPriority = this.card.board.priorities.find(x => x.id === this.card.priority);
    this.selectedPriority.priorityClass = this.convertToCamelCase(this.selectedPriority.id);
  }

  handlePriorityChange($event) {
    this.card.priority = $event;
    this.setSelectedPriority();
    this.updatePriority();
  }

  private updatePriority() {
    const updateTicketPriorityCommand =
      new PlatformCommand('UpdateTicketPriorityCommand', Api.TaskManager);
    updateTicketPriorityCommand.scope = this.card.scopeInt;
    updateTicketPriorityCommand.setValue('BoardId', this.card.board.id);
    updateTicketPriorityCommand.setValue('TicketId', this.card.id);
    updateTicketPriorityCommand.setValue('Priority', this.card.priority);

    this.commandService.executePlatformCommand(updateTicketPriorityCommand).pipe(take(1)).subscribe(response => {
      this.invalidateTaskViews();
    });
  }

  private convertToCamelCase(str: string): string {
    return str.charAt(0).toLowerCase() + str.slice(1);
  }

  // priorityEnd

  updateStatus(oldStatus) {
    const updateTaskCommand = new UpdateStatusCommand(this.card.id, this.card.status);
    updateTaskCommand.scope = this.card.scopeInt;
    this.commandService.executeCommand(updateTaskCommand).pipe(take(1)).subscribe({
      next: (response) => {
        this.invalidateTaskViews();
      },
      error: (err) => {
        this.card.status = oldStatus;
        console.log('Can not change status: ', err);
      }
    });
  }

  private invalidateTaskViews() {
    if (this.data.input.taskInvalidator) {
      this.data.input.taskInvalidator.next({card: this.card, sourceViewId: this.id});
    }
  }

  private setSelectedStatusList(currentStatusList: any[]) {
    this.statusList = [];
    let cnt = 0;
    currentStatusList.forEach(element => {
      element.id = cnt++;
      this.statusList.push(element);
    });
  }

  // date Functions
  setStartDateAndDueDate() {
    if (this.card && this.card.startDate) {
      this.startDate = this.timeZoneService.getDisplayDate(this.card.startDate);
    } else {
      this.startDate = null;
    }

    if (this.card && this.card.dueDate) {
      this.dueDate = this.timeZoneService.getDisplayDate(this.card.dueDate);
    } else {
      this.dueDate = null;
    }
  }

  isValidDate(date) {
    return date instanceof Date && !isNaN(date.getTime());
  }

  onStartDateClicked(taskMenuItem: any) {
    if (!this.hasPermission || this.isSupportBoard()) {
      return;
    }
    this.selectedMenuChange$.emit(taskMenuItem);
  }

  onDueDateClicked(taskMenuItem: any) {
    if (!this.hasPermission || this.isSupportBoard()) {
      return;
    }
    this.selectedMenuChange$.emit(taskMenuItem);
  }

  onMenuChange(taskMenuItem: IMenuItem) {
    this.selectedMenuChange$.emit(taskMenuItem);
  }

  copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text).then(() => {
        this.snackBarService.showInformation('URL copied to Clipboard!', 1500);
      }, (err) => {
        console.error('Could not copy text: ', err);
      }
    );
  }

  /*  onAcceptRequest() {
      const generateDummyCommand = new PlatformCommand('GenerateSupportTicketMagicLinkCommand', Api.TaskManager);
      generateDummyCommand.setValue('BoardId', this.card.board.id);
      generateDummyCommand.setValue('TicketId', this.card.id);
      this.commandService.executePlatformCommand(generateDummyCommand).pipe(take(1)).subscribe(response => {
        if (response && response.fieldValues && response.fieldValues['MagicLinkAccessInfo']) {
          this.magicLinkAccessInfo = response.fieldValues['MagicLinkAccessInfo'];
        }
      }, err => {
        console.log(err);
      });
    }*/

  onAcceptRequest() {
    const accessIssueCommand = new PlatformCommand('CrossCompanyAccessGrantedCommand', Api.AdminPanel);
    accessIssueCommand.setValue('AccessModelId', this.card.id);
    this.commandService.executePlatformCommand(accessIssueCommand).pipe(take(1)).subscribe(response => {
      if (response && response.fieldValues && response.fieldValues['CrossCompanyAccessGrantedModel']) {
        //this.crossCompanyAccessGrantedInfo = response.fieldValues['CrossCompanyAccessGrantedModel'];
        //this.crossCompanyAccessModelStatus = response.fieldValues['CrossCompanyAccessRequestModel'];
      }
    }, err => {
      console.log(err);
    });
  }

  /*  onRequestAccess() {
      if (this.magicLinkAccessInfo?.status === 'Pending') {
        this.snackBarService.showWarning('Another Support member has already requested for magic link');
      } else {
        const requestKanbanTicketMagicLinkCommand = new PlatformCommand('RequestKanbanTicketMagicLinkCommand', Api.TaskManager);
        requestKanbanTicketMagicLinkCommand.setValue('BoardId', this.card.board.id);
        requestKanbanTicketMagicLinkCommand.setValue('TicketId', this.card.id);
        this.commandService.executePlatformCommand(requestKanbanTicketMagicLinkCommand).pipe(take(1)).subscribe(response => {
          if (response && response.fieldValues && response.fieldValues['MagicLinkAccessInfo']) {
            this.magicLinkAccessInfo = response.fieldValues['MagicLinkAccessInfo'];
            if (this.magicLinkAccessInfo?.requestedUser) {
              this.isRequestedByMe = (this.magicLinkAccessInfo.requestedUser?.companyId.toLowerCase() === this.userInfo.companyId.toLowerCase() &&
                this.magicLinkAccessInfo.requestedUser?.email.toLowerCase() === this.userInfo.email.toLowerCase());
            }
          }
        }, err => {
          console.log(err);
        });
      }
    }*/

  onRequestAccess() {
    const crossCompanyAccessRequestCommand = new PlatformCommand('SupportAccessRequestCommand', Api.AdminPanel);
    crossCompanyAccessRequestCommand.setValue('RequestedTo', this.card.createdBy);
    crossCompanyAccessRequestCommand.setValue('RequestedBy', this.userInfo);
    crossCompanyAccessRequestCommand.setValue('TaskId', this.card.id);
    crossCompanyAccessRequestCommand.setValue('TicketDisplayId', this.card.displayId);

    this.commandService.executePlatformCommand(crossCompanyAccessRequestCommand).pipe(take(1)).subscribe(response => {
      if (response && response.fieldValues && response.fieldValues['SupportAccessModel']) {
        this.crossCompanyAccessModelStatus = response.fieldValues['SupportAccessModel'].status;
        this.invalidateCrossCompanyAccessInfo();
      }
    }, err => {
      this.snackBarService.showError('Access request failed!');
      console.log(err);
    });
  }

  private subscribeToInvalidatorListener() {
    this.taskManagerStore.pipe(select(getTaskViewUpdateInfo)).pipe(takeUntil(this.unsubscribe$)).subscribe(updatedTaskViewInfo => {
      if (updatedTaskViewInfo && updatedTaskViewInfo.card && updatedTaskViewInfo.card.id === this.card.id) {
        this.invalidateCard(updatedTaskViewInfo.card, updatedTaskViewInfo.sourceViewId);
      }
    });
  }

  private invalidateCard(updatedCard, sourceViewId: string) {
    this.card = updatedCard;
    this.getTaskMagicLinkAccessInfo();
    this.getCrossCompanyAccessStatusByCard();
    this.setStartDateAndDueDate();
    this.setPermission();
    this.setPermissionForSupportBoard(this.isSupportUser);
  }

  private getTaskMagicLinkAccessInfo() {
    const query = new PlatformQuery();
    query.name = 'KanbanTicketMagicLinkInfoQuery';
    query.api = Api.TaskManager;
    query.fieldValues['TicketId'] = this.card.id;
    query.fieldValues['BoardId'] = this.card.boardId;
    this.platformQueryService.executeQuery(query).pipe(take(1)).subscribe(response => {
      this.magicLinkAccessInfo = response.fieldValues['MagicLinkAccessInfo'];
      if (this.magicLinkAccessInfo?.requestedUser) {
        this.isRequestedByMe = (this.magicLinkAccessInfo.requestedUser?.companyId.toLowerCase() === this.userInfo.companyId.toLowerCase() &&
          this.magicLinkAccessInfo.requestedUser?.email.toLowerCase() === this.userInfo.email.toLowerCase());
      }
    });
  }

  private getCrossCompanyAccessStatusByCard() {
    const query = new PlatformQuery();
    query.name = 'SupportAccessStatusByCompanyQuery';
    query.api = Api.AdminPanel;
    query.fieldValues['CompanyId'] = this.card.createdBy.companyId;
    query.fieldValues['TaskId'] = this.card.id;
    query.fieldValues['RequestedByEmail'] = this.userInfo?.email;
    this.platformQueryService.executeQuery(query).pipe(take(1)).subscribe(response => {
      this.crossCompanyAccessModelStatus = response.fieldValues['SupportAccessModelStatus'] ?? null;
      this.invalidateCrossCompanyAccessInfo();
    });
  }

  private getCrossCompanyAccessGrantedInfoByCard() {
    const query = new PlatformQuery();
    query.name = 'CrossCompanyAccessGrantedQuery';
    query.api = Api.AdminPanel;
    query.fieldValues['AccessRequestId'] = this.card.id;
    this.platformQueryService.executeQuery(query).pipe(take(1)).subscribe(response => {
      //this.crossCompanyAccessGrantedInfo = response.fieldValues['CrossCompanyAccessGrantedModel'] ?? null;
    });
  }

  onOpenClientCompany() {
    const openCompanyCommand = new PlatformCommand('SupportAccessToClientCompanyCommand', Api.AdminPanel);
    openCompanyCommand.setValue('TicketId', this.card.id);
    openCompanyCommand.setValue('TicketDisplayId', this.card.displayId);
    openCompanyCommand.setValue('ClientCompanyId', this.card.createdBy.companyId);
    this.commandService.executePlatformCommand(openCompanyCommand).pipe(take(1)).subscribe(response => {
      if (response.fieldValues && response.fieldValues['RedirectUrl']) {
        this.authApiService.logoutUser(response.fieldValues['RedirectUrl']);
      }
    });
  }

  private subscribeToAccessRequestUpdate() {
    this.socketClusterService.subscribeToChannel(this.card.id).pipe().subscribe(res => {
      this.handleAccessRequestUpdate(res);
    });
  }

  private handleAccessRequestUpdate(res: any) {
    this.getCrossCompanyAccessStatusByCard();
  }

  private initializeCrossCompanyAccessInfo() {
    this.crossCompanyAccessInfo = {
      showRequestAccess: false,
      showGrantAccess: false,
      showRevokeAccess: false,
      showOpenCompany: false,
      showPendingAccess: false
    };
  }

  private isAssignedSupportMember(): boolean {
    const email = this.userInfo?.email?.toLowerCase();
    if (!email || !this.card?.assignments) {
      return false;
    }
    return this.card.assignments.some(assignment =>
      assignment.assignedTo?.companyId?.toLowerCase() === SupportCompanyId &&
      assignment.assignedTo?.email?.toLowerCase() === email);
  }

  private invalidateCrossCompanyAccessInfo() {
    this.initializeCrossCompanyAccessInfo();
    if (!this.isAssignedSupportMember()) {
      return;
    }
    if (this.crossCompanyAccessModelStatus === null
      || this.crossCompanyAccessModelStatus === SupportAccessStatus.Denied
      || this.crossCompanyAccessModelStatus === SupportAccessStatus.Revoked
      || this.crossCompanyAccessModelStatus === SupportAccessStatus.Expired) {
      this.crossCompanyAccessInfo.showRequestAccess = true;
    } else if (this.crossCompanyAccessModelStatus === SupportAccessStatus.Requested) {
      this.crossCompanyAccessInfo.showPendingAccess = true;
    } else if (this.crossCompanyAccessModelStatus === SupportAccessStatus.Granted) {
      this.crossCompanyAccessInfo.showOpenCompany = true;
    }
  }
}
export enum SupportAccessStatus {
  Pending = 0,
  Requested = 1,
  Granted = 2,
  Denied,
  Revoked,
  Expired
}
